import React from 'react';

type Props = {
  children: React.ReactNode;
  /** Nome curto da área ("tela", "aplicativo"), usado no texto da falha. */
  area?: string;
};

type State = { error: Error | null };

/**
 * Erro de render ou de efeito numa tela não derruba mais o app inteiro (antes a raiz ficava vazia, só o fundo
 * preto do body, sem menu para sair). Mostra o erro para o usuário mandar print e permite tentar de novo.
 * Use com `key` da rota para zerar ao trocar de tela.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const area = this.props.area || 'tela';
    return (
      <div
        role="alert"
        style={{
          margin: 24,
          padding: 20,
          borderRadius: 12,
          border: '1px solid var(--error-border, #7f1d1d)',
          background: 'var(--error-bg, #2a0f0f)',
          color: 'var(--text-primary, #ffffff)',
          maxWidth: 640,
        }}
      >
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Algo deu errado nesta {area}.</h2>
        <p style={{ margin: '0 0 12px', opacity: 0.85 }}>
          Tente de novo. Se continuar, mande um print desta mensagem para o suporte.
        </p>
        <pre
          style={{
            margin: '0 0 16px',
            padding: 10,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.25)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 12,
            maxHeight: 160,
            overflow: 'auto',
          }}
        >
          {`${error.name}: ${error.message}`}
        </pre>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="mai-btn" onClick={() => this.setState({ error: null })}>
            Tentar de novo
          </button>
          <button type="button" className="mai-btn" onClick={() => window.location.reload()}>
            Recarregar a página
          </button>
        </div>
      </div>
    );
  }
}
